import styled, { keyframes, css } from "styled-components";

const fadeInUp = keyframes`
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const fadeInAnimation = css`
  opacity: 0;
  animation: ${fadeIn} 0.8s ease forwards;
`;

// Apply animation with delay
export const createFadeInAnimation = (delay = 0) => css`
  ${fadeInAnimation}
  animation-delay: ${delay}s;
`;

// For mobile animations
export const createFadeInUpAnimation = (delay = 0) => css`
  opacity: 0;
  animation: ${fadeInUp} 0.8s ease forwards;
  animation-delay: ${delay}s;
`;

// Add keyframes for the left-to-right animation and dashed line drawing
const slideRightAnimation = keyframes`
  from {
    transform: translateX(-20px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const drawLineAnimation = keyframes`
  from {
    width: 0;
    opacity: 0;
  }
  to {
    width: 120%;
    opacity: 1;
  }
`;

const arrowAppearAnimation = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

export const ServiceRoot = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #1D1D1D;
  color: white;
  min-height: 100vh;
  padding: 0;

  @media (max-width: 768px) {
    padding: 0;
  }
`;

export const ServiceHeader = styled.div`
  width: 100%;
  max-width: 1440px;
  padding: 120px 20px 60px;
  text-align: center;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 80px 20px 40px;
  }
`;

export const ServiceTitle = styled.h1`
  font-size: 48px;
  font-weight: 700;
  margin-bottom: 24px;
`;

export const ServiceDescription = styled.p`
  color: #5C5C5C;
  font-size: 24px;
  font-family: Pretendard;
  font-weight: 400;
  line-height: 36px;
  word-wrap: break-word;
  margin: 0;
  
  @media (max-width: 768px) {
    font-family: Pretendard;
    font-weight: 400;
    font-size: 16px;
    line-height: 28px;
    letter-spacing: -0.01em;
  }
`;

export const ServiceContent = styled.div`
  width: 100%;
  padding: 0 240px;
  box-sizing: border-box;
  
  @media (max-width: 1920px) {
    padding: 0 240px;
  }
  
  @media (max-width: 1700px) {
    padding: 0 160px;
  }
  
  @media (max-width: 1440px) {
    padding: 0 80px;
  }
  
  @media (max-width: 768px) {
    padding: 0 20px;
  }

  /* Voucher container styles */
  .voucher-container {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 60px;
    ${createFadeInAnimation(0.2)}

    @media (max-width: 768px) {
      padding: 0;
      gap: 30px;
    }
  }

  .voucher-header {
    display: flex;
    flex-direction: column;
    gap: 40px;
    margin-top: 60px;
    
    @media (max-width: 768px) {
      gap: 0;
      margin-bottom: 30px;
      margin-top: 0;
    }
  }

  .voucher-title {
    font-size: 32px;
    font-family: Pretendard;
    font-weight: 600;
    line-height: 46px;
    word-wrap: break-word;
    margin: 0;

    @media (max-width: 768px) {
      font-family: Pretendard;
      font-weight: 700;
      font-size: 24px;
      line-height: 36px;
      letter-spacing: -0.01em;
    }
  }

  .voucher-title .highlight {
    color: #FF4F00;
  }

  .voucher-cards-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
    justify-content: center;
    ${createFadeInAnimation(0.4)}

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      padding: 0;
      width: 100%;
      gap: 20px;
      box-sizing: border-box;
    }
  }

  .voucher-card {
    width: 100%;
    height: 260px;
    position: relative;
    background: #F8F8F8;
    border-radius: 20px;
    padding: 36px 36px 40px 40px;
    overflow: hidden;
    box-sizing: border-box;
    display: flex;
    align-items: flex-end;
    transition: background 0.5s ease, box-shadow 0.5s ease;
    cursor: pointer;
    ${fadeInAnimation}
    
    &:nth-child(1) { animation-delay: 0.2s; }
    &:nth-child(2) { animation-delay: 0.4s; }
    &:nth-child(3) { animation-delay: 0.6s; }
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(180deg, #FFBC9E 0%, #FFFFFF 100%);
      border-radius: 20px;
      opacity: 0;
      transition: opacity 0.5s ease;
      z-index: 0;
    }
    
    &::after {
      content: '';
      position: absolute;
      bottom: -100px;
      left: -50px;
      width: 300px;
      height: 300px;
      background: linear-gradient(135deg, rgba(255, 241, 93, 0.35) 0%, rgba(255, 241, 93, 0) 80%);
      border-radius: 50%;
      opacity: 0;
      transition: opacity 0.5s ease, transform 0.5s ease;
      z-index: 0;
      pointer-events: none;
    }

    p {
      color: #1D1D1D;
      font-size: 24px;
      font-family: Pretendard;
      font-weight: 700;
      line-height: 36px;
      word-wrap: break-word;
      margin: 0;
      position: relative;
      z-index: 1;
      transition: color 0.5s ease;
    }

    .icon-container {
      width: 106px;
      height: 106px;
      position: absolute;
      right: 40px;
      top: 40px;
      overflow: visible;
      z-index: 1;
      transition: transform 0.5s ease;

      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        transition: transform 0.5s ease;
      }
    }
    
    @media (min-width: 769px) {
      &:hover {
        box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.1);
        
        &::before, &::after {
          opacity: 1;
        }

        &::after {
          transform: scale(1.1);
        }
        
        p {
          color: #FF4F00;
        }

        .icon-container {
          transform: scale(1.1);
          
          img {
            transform: scale(1.1);
          }
        }
      }
    }

    @media (max-width: 768px) {
      width: 100%;
      height: 156px;
      padding: 24px 24px 30px 30px;
      margin: 0;
      
      p {
        font-size: 18px;
        line-height: 28px;
      }

      .icon-container {
        width: 64px;
        height: 64px;
        right: 30px;
        top: 24px;
        overflow: visible;
      }

      &.active {
        box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.1);
        
        &::before, &::after {
          opacity: 1;
        }

        &::after {
          transform: scale(1.1);
        }
        
        p {
          color: #FF4F00;
        }

        .icon-container {
          transform: scale(1.1);
          
          img {
            transform: scale(1.1);
          }
        }
      }
    }
  }

  .voucher-description {
    color: #1D1D1D;
    font-size: 24px;
    font-family: Pretendard;
    font-weight: 400;
    line-height: 36px;
    word-wrap: break-word;
    margin: 0;
    
    @media (max-width: 768px) {
      font-size: 16px;
      font-weight: 500;
      line-height: 28px;
      
      br {
        display: none;
      }
    }
  }

  .process-section {
    width: 100vw;
    position: relative;
    left: 50%;
    right: 50%;
    margin-left: -50vw;
    margin-right: -50vw;
    margin-top: 140px;
    background-color: #F9F9F9;
    padding: 120px 0;
    overflow: visible;
    z-index: 0;

    @media (max-width: 1050px) {
      padding: 40px 0 70px 0;
      margin-top: 100px;
      overflow: visible;
    }
  }

  .process-inner {
    width: calc(100% - 480px);
    max-width: 1440px;
    margin: 0 auto;
    padding: 0;
    box-sizing: border-box;
    ${createFadeInAnimation(0.6)}
    position: relative;
    overflow: visible;
    
    @media (max-width: 1920px) {
      width: calc(100% - 480px);
    }
    
    @media (max-width: 1700px) {
      width: calc(100% - 320px);
    }
    
    @media (max-width: 1440px) {
      width: calc(100% - 160px);
    }

    @media (max-width: 1050px) {
      width: 100%;
      padding: 0;
      overflow: visible;
    }
    
    .step-label-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0;
      margin-bottom: 10px;
      
      .process-step-label {
        margin-bottom: 0;
      }
      
      .process-label {
        margin-top: 0;
      }
    }
  }

  .process-title {
    color: #1D1D1D;
    font-size: 48px;
    font-family: Montserrat;
    font-weight: 700;
    line-height: 70px;
    word-wrap: break-word;
    margin-bottom: 60px;
    margin-top: 0;

    @media (max-width: 1050px) {
      font-size: 32px;
      line-height: 40px;
      margin-bottom: 50px;
      padding: 0 0 0 20px;
    }
  }

  .process-steps-container {
    display: flex;
    flex-direction: row;
    gap: 70px;
    margin-top: 60px;
    position: relative;
    ${createFadeInAnimation(0.8)}
    overflow: visible;
    padding-left: 0;

    @media (min-width: 1051px) and (max-width: 1920px) {
      gap: 80px;
    }

    @media (min-width: 1051px) and (max-width: 1600px) {
      gap: 30px;
    }

    &::after {
      content: '';
      position: absolute;
      width: 120%;
      height: 2px;
      border-top: 2px dashed #999999;
      top: 50%;
      left: -20%;
      z-index: -1;
      animation: ${drawLineAnimation} 1.5s ease forwards;
      animation-delay: 0.8s;
    }

    &::before {
      content: '';
      position: absolute;
      width: 10px;
      height: 10px;
      border-top: 2px solid #999999;
      border-right: 2px solid #999999;
      transform: translateY(-50%) rotate(45deg);
      right: -5px;
      top: 50%;
      z-index: -1;
      opacity: 0;
      animation: ${arrowAppearAnimation} 2s ease forwards;
      animation-delay: 1.8s;
    }

    @media (max-width: 1050px) {
      display: none;
    }
  }
  
  /* Process slider for mobile */
  .process-slider-container {
    display: flex !important;
    flex-direction: row !important;
    overflow-x: auto !important;
    width: 100% !important;
    scroll-behavior: smooth !important;
    /* 스크롤바 숨기기 */
    scrollbar-width: none !important; /* Firefox */
    -ms-overflow-style: none !important; /* IE, Edge */
  }
  .process-slider-container::-webkit-scrollbar {
    display: none !important;
  }
  
  .process-slide {
    flex: 0 0 100% !important;
    width: 100% !important;
    scroll-snap-align: start;
    padding: 20px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    position: relative;
    transition: transform 0.3s ease;
    
    @media (max-width: 1050px) {
      gap: 0;
      overflow: visible;
      padding: 20px 0;
      scroll-snap-align: center;
      min-width: 100%;
    }
    
    .process-content {
      flex-direction: row;
      justify-content: center;
      gap: 20px;
      margin: 20px 0;
      width: 100%;
      display: flex;
      position: relative;
      
      @media (max-width: 1050px) {
        gap: 15px;
        margin-top: 40px;
        overflow: visible;
        padding: 0;
        justify-content: center;
        align-items: center;
        z-index: 3;
        
        /* process-item이 찌그러지지 않도록 스타일 적용 */
        .process-item {
          width: 160px;
          height: 160px;
          min-width: 160px;
          min-height: 160px;
          max-width: 160px;
          max-height: 160px;
          flex-shrink: 0;
          box-sizing: border-box !important;
          border-radius: 50%;
          margin: 0;
          padding: 0;
          z-index: 5;
          position: relative;
        }
        
        &::before {
          content: '';
          position: absolute;
          width: 100%;
          height: 2px;
          border-top: 2px dashed #999999;
          top: 50%;
          right: auto;
          left: 0;
          transform: none;
          z-index: 1;
        }
      }
    }
    
    &:nth-child(3) {
      .process-content {
        @media (max-width: 1050px) {
          &::after {
            content: '';
            position: absolute;
            width: 10px;
            height: 10px;
            border-top: 2px solid #999999;
            border-right: 2px solid #999999;
            transform: translateY(-50%) rotate(45deg);
            right: 20px;
            top: 50%;
            z-index: 1; /* 원보다 낮은 z-index로 뒤로 배치 */
          }
          
          &::before {
            width: calc(100% - 20px);
            left: 0;
            transform: none;
            z-index: 1; /* 원보다 낮은 z-index로 뒤로 배치 */
          }
        }
      }
    }
    
    @media (max-width: 1050px) {
      gap: 0;
      overflow: visible;
      padding: 20px 0;
    }
  }
  
  .process-pagination {
    display: none;
    justify-content: center;
    align-items: center;
    gap: 10px;
    margin-top: 30px;
    
    @media (max-width: 1050px) {
      display: flex;
    }
  }
  
  .process-pagination-indicator {
    width: 32px;
    height: 3px;
    background-color: #DCDCDC;
    cursor: pointer;
    transition: background-color 0.3s ease;
    
    &.active {
      background-color: #FF4F00;
    }
  }

  .process-step {
    flex: 1;
    padding: 20px 20px 32px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    opacity: 0;
    animation: ${slideRightAnimation} 0.5s ease forwards;
    position: relative;
    z-index: 1;
    
    @media (min-width: 1051px) and (max-width: 1920px) {
      padding: 15px 15px 25px;
      gap: 8px;
    }
    
    @media (min-width: 1051px) and (max-width: 1600px) {
      padding: 15px 10px 25px;
      gap: 6px;
    }
    
    &:nth-child(1) { 
      animation-delay: 1s; 
      padding-left: 42px;
      
      @media (min-width: 1051px) and (max-width: 1920px) {
        padding-left: 42px;
      }
      
      @media (min-width: 1051px) and (max-width: 1600px) {
        padding-left: 20px;
      }
      
      @media (min-width: 1051px) and (max-width: 1400px) {
        padding-left: 10px;
      }
      
      @media (min-width: 1051px) and (max-width: 1200px) {
        padding-left: 0;
      }
    }
    &:nth-child(2) { animation-delay: 1.3s; }
    &:nth-child(3) { 
      animation-delay: 1.6s; 
      padding-right: 25px;
      
      @media (min-width: 1051px) and (max-width: 1920px) {
        padding-right: 20px;
      }
      
      @media (min-width: 1051px) and (max-width: 1600px) {
        padding-right: 15px;
      }
      
      @media (min-width: 1051px) and (max-width: 1400px) {
        padding-right: 10px;
      }
      
      @media (min-width: 1051px) and (max-width: 1200px) {
        padding-right: 0;
      }
    }
  }

  .process-content {
    position: relative;
    justify-content: center;
    align-items: center;
    gap: 16px;
    display: inline-flex;
    width: 100%;
    z-index: 2;
    
    @media (min-width: 769px) and (max-width: 1600px) {
      gap: 20px;
    }
  }

  .process-item {
    width: 220px;
    height: 220px;
    background: white;
    border-radius: 50%;
    border: 2px solid #F0F0F0;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    z-index: 2;
    transform: scale(0.8);
    opacity: 0;
    animation: appear 0.5s ease forwards;
    box-sizing: border-box;
    
    @media (min-width: 1051px) and (max-width: 1600px) {
      width: 180px;
      height: 180px;
    }
    
    @media (min-width: 1051px) and (max-width: 1400px) {
      width: 160px;
      height: 160px;
    }
    
    @keyframes appear {
      from {
        transform: scale(0.8);
        opacity: 0;
      }
      to {
        transform: scale(1);
        opacity: 1;
      }
    }

    &:nth-child(1) { animation-delay: 1.2s; }
    &:nth-child(2) { 
      animation-delay: 1.4s; 
      width: 220px; /* 220x220 크기로 통일 */
      
      @media (min-width: 1051px) and (max-width: 1600px) {
        width: 180px;
        height: 180px;
      }
      
      @media (min-width: 1051px) and (max-width: 1400px) {
        width: 160px;
        height: 160px;
      }
    }

    span {
      color: #1D1D1D;
      font-family: Pretendard;
      font-weight: 700;
      font-size: 20px;
      line-height: 28px;
      letter-spacing: -0.01em;
      text-align: center;
      vertical-align: middle;
      word-wrap: break-word;
      padding: 0 30px;
      
      @media (min-width: 1051px) and (max-width: 1600px) {
        font-size: 18px;
        line-height: 26px;
        padding: 0 20px;
      }
      
      @media (min-width: 1051px) and (max-width: 1400px) {
        font-size: 16px;
        line-height: 24px;
        padding: 0 15px;
      }
    }
    
    @media (max-width: 1050px) {
      width: 160px;
      height: 160px;
      min-width: 160px;
      min-height: 160px;
      max-width: 160px;
      max-height: 160px;
      animation: none;
      transform: none;
      opacity: 1;
      flex-shrink: 0;
      position: relative;
      z-index: 5; /* 점선보다 앞에 표시되도록 z-index 설정 */
      background-color: white;
      box-sizing: border-box !important;
      border: 2px solid #F0F0F0;
      border-radius: 50%;
      margin: 0;
      padding: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      
      span {
        font-family: Pretendard;
        font-weight: 700;
        font-size: 16px;
        line-height: 28px;
        letter-spacing: -0.01em;
        text-align: center;
        vertical-align: middle;
        padding: 0 15px;
      }
    }
  }

  .process-label {
    color: #5C5C5C;
    font-size: 16px;
    font-family: Montserrat;
    font-weight: 700;
    line-height: 28px;
    word-wrap: break-word;
    opacity: 0;
    animation: fadeIn 0.5s ease forwards;
    animation-delay: 2s;
    
    @keyframes fadeIn {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }
    
    @media (max-width: 1050px) {
      opacity: 1;
      animation: none;
      margin-top: 0;
    }
  }

  .process-step-label {
    color: #FF4F00;
    font-size: 16px;
    font-family: Montserrat;
    font-weight: 700;
    line-height: 28px;
    word-wrap: break-word;
    margin-bottom: 20px;
    opacity: 0;
    animation: fadeIn 0.5s ease forwards;
    
    &:nth-child(1) { animation-delay: 1.1s; }
    &:nth-child(2) { animation-delay: 1.4s; }
    &:nth-child(3) { animation-delay: 1.7s; }
    
    @media (max-width: 1050px) {
      opacity: 1;
      animation: none;
      margin-bottom: 0;
    }
  }

  .voucher-what-we-do-section {
    width: 100%;
    margin-top: 60px;
    margin-bottom: 200px;
    ${createFadeInAnimation(0.8)}

    @media (max-width: 768px) {
      padding: 0;
      margin-top: 100px;
      margin-bottom: 130px;
    }
  }

  .voucher-what-we-do-title {
    color: #1D1D1D;
    font-size: 48px;
    font-family: Montserrat;
    font-weight: 700;
    line-height: 70px;
    word-wrap: break-word;
    margin-bottom: 60px;
    margin-top: 0;

    @media (max-width: 768px) {
      font-size: 32px;
      line-height: 40px;
      margin-bottom: 40px;
    }
  }

  .voucher-what-we-do-container {
    display: flex;
    justify-content: space-between;
    gap: 50px;
    width: 100%;

    @media (max-width: 1200px) {
      gap: 30px;
    }
    
    @media (max-width: 1040px) {
      flex-direction: column;
      gap: 60px;
    }

    @media (max-width: 768px) {
      flex-direction: column;
      padding: 0;
      gap: 30px;
    }
  }

  .voucher-what-we-do-list {
    width: 680px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 60px;
    
    @media (max-width: 1919px) {
      width: 40%;
      min-width: 400px;
      max-width: 680px;
      flex-shrink: 1;
    }
    
    @media (max-width: 1200px) {
      min-width: 350px;
    }
    
    @media (max-width: 1040px) {
      width: 100%;
      min-width: unset;
      max-width: 100%;
    }

    @media (max-width: 768px) {
      width: 100%;
      min-width: unset;
      gap: 60px;
    }
  }

  .voucher-what-we-do-item {
    display: flex;
    flex-direction: column;
    gap: 12px;
    cursor: pointer;
    transition: all 0.3s ease;
    ${fadeInAnimation}
    
    &:nth-child(1) { animation-delay: 0.2s; }
    &:nth-child(3) { animation-delay: 0.4s; }
    &:nth-child(5) { animation-delay: 0.6s; }

    &:hover .voucher-number,
    &:hover .voucher-heading {
      color: #FF4F00;
    }
    
    &.active .voucher-number {
      color: #FF4F00;
    }
    
    &.active .voucher-heading {
      color: #FF4F00;
    }
    
    &:not(.active) .voucher-number {
      color: #999999;
    }
    
    &:not(.active) .voucher-heading {
      color: #1D1D1D;
    }
    
    @media (max-width: 768px) {
      padding: 0;
      border-radius: 12px;
      gap: 10px;
      
      &.active {
        background-color: transparent;
      }
      
      &.active .voucher-number {
        color: #999999;
      }
      
      &.active .voucher-heading {
        color: #1D1D1D;
      }
    }
  }

  .voucher-what-we-do-item-title {
    display: flex;
    flex-direction: column;
    gap: 2px;
    width: calc(100% + 47px);
    margin-left: -47px;
    padding-left: 47px;
    box-sizing: border-box;

    @media (max-width: 768px) {
      width: calc(100% + 34px);
      margin-left: -17px;
      padding-left: 17px;
    }
  }

  .voucher-what-we-do-divider {
    width: 100%;
    height: 1px;
    background: #DCDCDC;
    margin: 0;
  }

  .voucher-number {
    color: #FF4F00;
    font-size: 16px;
    font-family: Montserrat;
    font-weight: 500;
    line-height: 28px;
    transition: color 0.3s ease;
  }

  .voucher-heading {
    color: #1D1D1D;
    font-size: 32px;
    font-family: Montserrat;
    font-weight: 700;
    line-height: 40px;
    transition: color 0.3s ease;
    letter-spacing: -1%;


    @media (max-width: 768px) {
      font-size: 24px;
      line-height: 36px;
    }
  }

  .voucher-what-we-do-description {
    color: #1D1D1D;
    font-size: 16px;
    font-family: Pretendard;
    font-weight: 400;
    line-height: 28px;
    letter-spacing: -0.015em;
    @media (max-width: 768px) {
      letter-spacing: -0.04em;
      font-weight: 300;
    }
  }

  .voucher-what-we-do-content {
    width: 710px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 50px;
    opacity: 1;
    transition: opacity 0.3s ease;
    
    @media (max-width: 1919px) {
      width: 55%;
      min-width: 400px;
      flex-shrink: 1;
    }
    
    @media (max-width: 1200px) {
      min-width: 350px;
    }
    
    @media (max-width: 1040px) {
      width: 100%;
      min-width: unset;
    }

    @media (max-width: 768px) {
      width: 100%;
      min-width: unset;
      gap: 30px;
    }
  }

  .voucher-what-we-do-image {
    width: 100% !important;
    height: auto !important;
    min-width: unset;
    min-height: unset;
    border-radius: 20px;
    object-fit: cover;
    transition: opacity 0.3s ease;
    aspect-ratio: 710/335;

    @media (max-width: 1040px) {
      width: 100% !important;
      min-width: unset;
      height: auto !important;
      min-height: unset;
      margin-bottom: 20px;
    }

    @media (max-width: 768px) {
      width: 100% !important;
      min-width: unset;
      height: 167px !important;
      min-height: unset;
      margin-bottom: 20px;
    }
  }

  .voucher-what-we-do-text {
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
    white-space: normal;
    word-wrap: break-word;
    word-break: keep-all;

    h3 {
      color: #1D1D1D;
      font-size: 24px;
      font-family: Pretendard;
      font-weight: 700;
      line-height: 36px;
      margin: 0;
      letter-spacing: -0.02em;
    }

    p {
      color: #1D1D1D;
      font-size: 16px;
      font-family: Pretendard;
      font-weight: 400;
      line-height: 28px;
      margin: 0;
    }
    
    @media (max-width: 768px) {
      h3 {
        font-size: 24px;
        line-height: 36px;
      }
      
      p {
        font-size: 16px;
        line-height: 28px;
        
        br {
          display: none;
        }
      }
    }
  }

  .voucher-what-we-do-bottom {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 40px;
  }

  .voucher-what-we-do-button {
    width: 222px;
    height: 60px;
    padding: 10px 30px;
    border-radius: 50px;
    border: 1px solid #DCDCDC;
    background: transparent;
    color: #1D1D1D;
    font-size: 15px;
    font-family: Pretendard;
    font-weight: 500;
    line-height: 28px;
    word-wrap: break-word;
    letter-spacing: -0.02em;
    cursor: pointer;
    transition: all 0.3s ease;
    align-self: flex-start;
    white-space: nowrap;

    &:hover {
      color: #FF4F00;
    }
    
    @media (max-width: 768px) {
      width: 100%;
      max-width: 100%;
      align-self: center;
      text-align: center;
      justify-content: center;
      display: flex;
      align-items: center;
      font-size: 16px;
      font-weight: 400;
    }
  }
`;

export const WhatWeDoContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    padding: 0;
    gap: 40px;
    margin-bottom: 0;
  }
`;

export const WhatWeDoTitle = styled.h2`
  color: #1D1D1D;
  font-size: 48px;
  font-family: Montserrat;
  font-weight: 700;
  line-height: 70px;
  word-wrap: break-word;
  margin: 60px 0;
  ${createFadeInAnimation(0.2)}

  @media (max-width: 768px) {
    font-size: 32px;
    line-height: 40px;
    margin: 0;
  }
`;

export const ServicesGrid = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 0;
  position: relative;
  width: 100%;
  justify-content: center;
  ${createFadeInAnimation(0.4)}

  @media (min-width: 1920px) {
    max-width: 1440px;
    margin: 0 auto;
    margin-bottom: 300px;
  }

  @media (min-width: 769px) {
    padding: 0 20px;
    box-sizing: border-box;
    margin-bottom: 300px;
  }

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 40px;
    max-width: none;
    padding: 0;
    margin-bottom: 0;
  }
`;

export const ServiceCard = styled.div`
  flex: 1;
  min-width: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  position: relative;
  ${createFadeInAnimation(0.3)}

  @media (min-width: 1920px) {
    flex: 0 0 448px;
    width: 448px;
  }

  @media (min-width: 769px) {
    .text-content {
      height: 144px;
      display: flex;
      flex-direction: column;
    }
  }

  @media (max-width: 768px) {
    min-width: 100%;
    max-width: 100%;
    padding: 0;
  }

  &:nth-child(1) {
    padding-right: 24px;
  }

  &:nth-child(2) {
    padding-left: 24px;
    padding-right: 24px;
  }

  &:nth-child(3) {
    padding-left: 24px;
  }

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: 1px;
    background-color: #DCDCDC;
  }

  @media (max-width: 768px) {
    &:nth-child(1) {
      padding-right: 0;
    }

    &:nth-child(2) {
      padding-left: 0;
      padding-right: 0;
    }

    &:nth-child(3) {
      padding-left: 0;
    }

    &:not(:last-child) {
      padding-bottom: 40px;
      border-bottom: 1px solid #DCDCDC;
    }

    &:not(:last-child)::after {
      display: none;
    }
  }
`;

export const ProjectCountText = styled.span`
  color: #1D1D1D;
  font-size: 14px;
  font-family: "Montserrat", sans-serif;
  line-height: 24px;
  word-wrap: break-word;
`;

export const CircleContainer = styled.div`
  position: relative;
  width: 272px;
  height: 208px;
`;

export const Circle = styled.div`
  width: 133.62px;
  height: 134.51px;
  position: absolute;
  border-radius: 9999px;
  border: 3px #FF4F00 solid;

  &.top-left {
    left: 32px;
    top: 0px;
  }

  &.top-right {
    left: 106.38px;
    top: 0px;
  }

  &.bottom-left {
    left: 32px;
    top: 73.49px;
  }

  &.bottom-right {
    left: 106.38px;
    top: 73.49px;
  }
`;

export const CardTitle = styled.span`
  color: #1D1D1D;
  font-size: 28px;
  font-family: Montserrat;
  font-weight: 700;
  line-height: 36px;
  word-wrap: break-word;
  margin: 0;
  position: relative;
  z-index: 1;
  font-family: Montserrat;
  font-weight: 700;
  font-size: 28px;
  line-height: 36px;
  letter-spacing: -0.01em;
  padding: 0;
  border-top: 0;
  box-sizing: border-box;

  @media (max-width: 768px) {
    font-family: Montserrat;
    font-weight: 700;
    font-size: 32px;
    line-height: 40px;
    letter-spacing: -0.01em;
    width: 100%;
  }
`;

// Type of Work Cards
export const ServiceCardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-top: 40px;
  width: 100%;
  
  @media (max-width: 1300px) {
    grid-template-columns: repeat(2, 1fr);
    max-width: 800px;
    margin-left: auto;
    margin-right: auto;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

export const CardSliderContainer = styled.div`
  display: none;
  width: 100%;
  
  @media (max-width: 768px) {
    display: block;
    margin-top: 20px;
    margin-bottom: 0;
    overflow: visible;
    position: relative;
    padding: 0;
    margin-left: -20px;
    margin-right: -20px;
    width: calc(100% + 40px);
  }
`;

export const CardContainer = styled.div`
  display: none;
  
  @media (max-width: 1300px) {
    display: flex;
    width: 100%;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    scrollbar-width: none;
    -ms-overflow-style: none;
    -webkit-overflow-scrolling: touch;
    scroll-behavior: smooth;
    gap: 0;
    padding: 20px 20px;
    box-sizing: border-box;
    
    /* 모멘텀 스크롤 효과 적용 */
    -webkit-overflow-scrolling: touch;
    
    /* 터치 동작 최적화 */
    touch-action: pan-x;
    
    &::-webkit-scrollbar {
      display: none;
    }
    
    /* Card styling for the container */
    & > div {
      flex: 0 0 calc(100vw - 40px);
      scroll-snap-align: center;
      width: calc(100vw - 40px);
      box-sizing: border-box;
      margin-right: 20px;
      
      @media (min-width: 769px) {
        flex: 0 0 400px;
        width: 400px;
      }
      
      /* 모바일에서는 카드 전환 효과 제거 */
      transition: none;
      transform: none !important;
      
      &:hover {
        transform: none !important;
      }
      
      &:last-child {
        margin-right: 20px;
      }
    }
  }
`;

export const CardPageIndicator = styled.div`
  display: none;
  
  @media (max-width: 1300px) {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    margin-top: 40px;
  }
`;

export const CardIndicatorDot = styled.div<{ active?: boolean }>`
  width: 32px;
  height: 3px;
  background-color: ${props => props.active ? '#FF4F00' : '#DCDCDC'};
  transition: background-color 0.3s ease;
  cursor: pointer;
`;

export const ServiceCardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 20px;
  margin-bottom: 22px;

  @media (max-width: 768px) {
    margin-bottom: 0;
  }
`;

export const ServiceNumber = styled.span`
  color: #FF4F00;
  font-size: 24px;
  font-weight: 700;
  line-height: 36px;
  padding-top: 2px;
`;

export const ServiceCardTitle = styled.h3`
  color: #1D1D1D;
  font-size: 24px;
  font-weight: 700;
  line-height: 36px;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 24px;
    line-height: 36px;
    font-family: Pretendard;
    letter-spacing: -0.01em;
  }
`;

export const ServiceCardDescription = styled.p`
  color: #1D1D1D;
  font-size: 16px;
  font-family: Pretendard;
  font-weight: 400;
  line-height: 28px;
  word-wrap: break-word;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 16px;
    line-height: 28px;
    margin-top: 16px;
  }
`;

export const IconContainer = styled.div`
  width: 100px;
  height: 100px;
  display: flex;
  justify-content: center;
  align-items: center;

  img {
    width: 100px;
    height: 100px;
    object-fit: contain;
  }

  @media (min-width: 769px) {
    position: static;
    margin-top: 22px;
  }

  @media (max-width: 768px) {
    position: relative;
    bottom: auto;
    right: auto;
    margin-top: 48px;
  }
`;

export const WorkSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 40px;
  ${createFadeInAnimation(0.6)}
  
  @media (max-width: 768px) {
    padding: 0;
    gap: 0;
    margin-top: 90px;
  }
`;

export const WorkSectionHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 70px;

  @media (max-width: 768px) {
    height: auto;
    flex-direction: column;
    align-items: flex-start;
    gap: 0px;
    margin-bottom: 0px;
  }
`;

export const TypeOfWorkTitle = styled.span`
  color: #1D1D1D;
  font-size: 48px;
  font-family: Montserrat;
  font-weight: 700;
  line-height: 70px;
  word-wrap: break-word;
  margin-top: 0;

  @media (max-width: 768px) {
    font-size: 32px;
    line-height: 40px;
    margin-bottom: 0 !important;
    margin-top: 0;
  }
`;

export const SeeAllButton = styled.button`
  height: 60px;
  min-width: 160px;
  padding: 10px 30px;
  border-radius: 50px;
  border: 1px solid #DCDCDC;
  background: transparent;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  
  span {
    color: #1D1D1D;
    font-size: 16px;
    font-family: Montserrat;
    font-weight: 500;
    line-height: 28px;
    word-wrap: break-word;
    transition: color 0.3s ease;
  }

  &:hover {
    span {
      color: #FF4F00;
    }
  }

  &.desktop-only {
    display: none;
  }

  &.mobile-only {
    display: block;
    margin-top: 60px;
    margin-bottom: 50px;
  }

  @media (min-width: 769px) {
    &.desktop-only {
      display: block;
    }

    &.mobile-only {
      display: none;
    }
  }
  
  @media (max-width: 768px) {
    width: 100%;
    max-width: 100%;
  }
`;

export const TypeOfWorkCard = styled.div`
  width: 100%;
  min-height: 420px;
  padding: 36px;
  background: linear-gradient(180deg, #FBE3D4 0%, rgba(251, 227, 212, 0.8) 40%, white 90%);
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.10);
  border-radius: 28px;
  border: 1px solid #F0F0F0;
  display: flex;
  flex-direction: column;
  position: relative;
  transition: transform 0.5s ease, box-shadow 0.5s ease, background 0.5s ease;
  overflow: hidden;
  cursor: pointer;
  box-sizing: border-box;
  ${fadeInAnimation}
  
  @media (max-width: 1200px) {
    border-radius: 24px;
  }
  
  &:nth-child(1) { animation-delay: 0.2s; }
  &:nth-child(2) { animation-delay: 0.4s; }
  &:nth-child(3) { animation-delay: 0.6s; }
  &:nth-child(4) { animation-delay: 0.8s; }
  
  &::before {
    content: '';
    position: absolute;
    top: -150px;
    right: -150px;
    width: 400px;
    height: 400px;
    background: radial-gradient(circle, rgba(123, 97, 255, 0.3) 0%, rgba(123, 97, 255, 0) 70%);
    border-radius: 50%;
    opacity: 0;
    transition: opacity 0.5s ease;
    z-index: 0;
  }

  .header-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    width: 100%;
    position: relative;
    z-index: 10;
  }

  ${ProjectCountText} {
    color: #999999;
    font-size: 16px;
    font-family: Montserrat;
    font-weight: 500;
    line-height: 28px;
    word-wrap: break-word;
  }

  .go-to-list-area {
    position: absolute;
    top: -15px;
    right: -15px;
    width: 100px;
    height: 60px;
    display: none;
    justify-content: flex-end;
    align-items: flex-start;
    padding: 15px;
    z-index: 10;
    cursor: pointer;
  }

  .go-to-list {
    color: #1D1D1D;
    font-size: 16px;
    font-family: Montserrat;
    font-weight: 500;
    line-height: 28px;
    word-wrap: break-word;
    z-index: 10;
    position: relative;
    
    &:hover {
      color: #FF4F00;
      text-decoration: underline;
    }
  }

  .content-wrapper {
    display: flex;
    flex-direction: column;
    flex: 1;
    position: relative;
    width: 100%;
    box-sizing: border-box;
    margin-top: 0;
  }

  img {
    width: 208px;
    height: 208px;
    max-width: 208px;
    aspect-ratio: 1 / 1;
    object-fit: contain;
    margin: auto;
    position: relative;
    transition: opacity 0.5s ease;
    z-index: 1;
    display: block;

    @media (max-width: 768px) {
      width: 168px;
      height: 168px;
      max-width: none;
      max-height: none;
      margin: auto;
      position: relative;
      top: auto;
      left: auto;
      transform: none;
    }
  }

  ${CardTitle} {
    position: relative;
    margin: 0;
    z-index: 1;
    font-family: Montserrat;
    font-weight: 700;
    font-size: 28px;
    line-height: 36px;
    letter-spacing: -0.01em;
    padding: 0;
    border-top: 0;
    box-sizing: border-box;

    @media (max-width: 768px) {
      font-family: Montserrat;
      font-weight: 700;
      font-size: 32px;
      line-height: 40px;
      letter-spacing: -0.01em;
      width: 100%;
    }
  }

  .content-text {
    display: none;
    opacity: 0;
    margin: 0;
    width: 100%;
    position: relative;
    z-index: 1;
    padding: 0;
    color: #1D1D1D;
    font-family: Montserrat;
    font-weight: 700;
    font-size: 16px;
    line-height: 28px;
    letter-spacing: -0.01em;

    span {
      display: block;
      position: relative;
      padding-left: 12px;
      margin-bottom: 0;

      &:last-child {
        margin-bottom: 0;
      }

      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 12px;
        width: 4px;
        height: 4px;
        background: #1D1D1D;
        border-radius: 50%;
      }

      @media (max-width: 768px) {
        margin-bottom: 0;
        font-family: Montserrat;
      }
    }
  }

  /* PC 호버 효과 */
  @media (min-width: 769px) {
    &:hover {
      transform: translateY(-20px);
      background: linear-gradient(180deg, #FBE3D4 0%, rgba(251, 227, 212, 0.8) 40%, white 90%);

      &::before {
        opacity: 1;
      }

      img {
        opacity: 0;
        display: none;
      }

      .header-row {
        margin-bottom: 20px;
      }

      .content-wrapper {
        display: flex;
        flex-direction: column;
        height: 100%;
      }

      .content-text {
        display: block;
        opacity: 1;
        margin: 0;
      }
      
      .divider-line {
        display: none;
      }
      
      ${CardTitle} {
        padding-top: 20px;
        margin-top: auto;
        border-top: 1px solid #DCDCDC;
      }
      
      .go-to-list-area {
        display: flex;
      }
    }
  }

  /* 모바일 스타일 */
  @media (max-width: 768px) {
    &.active {
      background: linear-gradient(180deg, #FBE3D4 0%, rgba(251, 227, 212, 0.8) 40%, white 90%);

      &::before {
        opacity: 1;
      }

      ${CardTitle} {
        padding-top: 20px;
        margin-top: auto;
        border-top: 1px solid #DCDCDC;
      }

      .content-wrapper {
        display: flex;
        flex-direction: column;
        height: 100%;
      }

      img {
        opacity: 0;
        display: none;
      }

      .content-text {
        display: block;
        opacity: 1;
        margin: 0;
      }

      .header-row {
        margin-bottom: 20px;
      }
      
      .go-to-list-area {
        display: flex;
      }
    }
  }
`;

export const ScopeOfWorkSection = styled.div`
  width: 100%;
  margin-top: 120px;
  margin-bottom: 300px;
  ${createFadeInAnimation(1)}

  @media (max-width: 768px) {
    margin-top: 90px;
    margin-bottom: 130px;
  }
`;

export const ScopeOfWorkTitle = styled.h2`
  color: #1D1D1D;
  font-size: 48px;
  font-family: Montserrat;
  font-weight: 700;
  line-height: 70px;
  word-wrap: break-word;

  @media (max-width: 768px) {
    font-size: 32px;
    line-height: 40px;
  }
`;

export const ScopeOfWorkGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(300px, 467px));
  grid-template-areas: 
    "wide1 wide1 card3"
    "card4 card5 card6"
    "card7 card8 card9"
    "card10 wide3 wide3"
    ;
  gap: 20px;
  margin-top: 60px;
  justify-content: center;
  
  @media (min-width: 769px) and (max-width: 1600px) {
    grid-template-columns: repeat(3, minmax(250px, 1fr));
  }

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    grid-template-columns: unset;
    grid-template-areas: unset;
    padding: 0;
    max-width: 100%;
    gap: 20px;
    margin-top: 40px;

    /* 모바일에서 카드 순서 조정 */
    & > div:first-child {  /* Search AD */
      order: 1;
    }
    & > div:nth-child(2) {  /* Display AD */
      order: 2;
    }
    & > div:nth-child(3) {  /* Short Form AD */
      order: 3;
    }
    & > div:nth-child(4) {  /* Influencer */
      order: 4;
    }
    & > div:nth-child(5) {  /* SNS Power Page */
      order: 5;
    }
    & > div:nth-child(6) {  /* Community Viral */
      order: 6;
    }
    & > div:nth-child(7) {  /* App Marketing */
      order: 7;
    }
    & > div:nth-child(8) {  /* Network AD */
      order: 8;
    }
    & > div:last-child {  /* OOH */
      order: 9;
    }
    & > div:nth-last-child(2) {  /* ATL */
      order: 10;
    }
  }
`;

export const ScopeCard = styled.div<{ $isWide?: boolean }>`
  width: 100%;
  height: 260px;
  padding: 47px 0 0 47px;
  background: #F8F8F8;
  border-radius: 24px;
  outline: 1px solid #DCDCDC;
  outline-offset: -1px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  position: relative;
  overflow: hidden;
  transition: background 0.5s ease;
  box-sizing: border-box;
  ${fadeInAnimation}
  
  &:first-child {
    grid-area: wide1;
  }

  &:nth-last-child(1) {
    grid-area: wide3;
  }

  &:nth-child(1) { animation-delay: 0.1s; }
  &:nth-child(2) { animation-delay: 0.2s; }
  &:nth-child(3) { animation-delay: 0.3s; }
  &:nth-child(4) { animation-delay: 0.4s; }
  &:nth-child(5) { animation-delay: 0.5s; }
  &:nth-child(6) { animation-delay: 0.6s; }
  &:nth-child(7) { animation-delay: 0.7s; }
  &:nth-child(8) { animation-delay: 0.8s; }
  &:nth-child(9) { animation-delay: 0.9s; }
  &:nth-child(10) { animation-delay: 1.0s; }

  .scope-card-title {
    position: relative;
    z-index: 2;
    margin-bottom: 0;

    &.pc-title {
      @media (max-width: 768px) {
        display: none;
      }
    }
    
    &.mobile-title {
      display: none;
      
      @media (max-width: 768px) {
        display: block;
        white-space: normal;
        br {
          display: block !important;
        }
      }
    }
  }

  .title-description-gap {
    height: 20px;
    width: 100%;
    display: block;
    position: relative;
    z-index: 2;
  }

  .scope-description {
    position: absolute;
    left: 50px;
    right: 50px;
    top: 106px;
    opacity: 0;
    color: #1D1D1D;
    font-family: Pretendard;
    font-weight: 400;
    font-size: 16px;
    line-height: 28px;
    letter-spacing: -0.01em;
    transition: opacity 0.5s ease;
    pointer-events: none;
    z-index: 2;
    white-space: normal;
    word-wrap: break-word;
    word-break: keep-all;
    
    p {
      margin-top: 0;
      white-space: normal;
      word-wrap: break-word;
      word-break: keep-all;
      font-family: Pretendard;
      font-weight: 400;
      font-size: 16px;
      line-height: 28px;
      letter-spacing: -0.01em;
    }
    
    @media (max-width: 768px) {
      position: static;
      margin-top: 16px;
      left: auto;
      right: auto;
      top: auto;
      bottom: auto;
      max-width: 295px;
      width: 295px;
      text-align: left;
      padding: 0;
      font-size: 14px;
      font-weight: 400;
      line-height: 26px;
      transition: opacity 0.3s ease;
      align-self: flex-start;
      
      p {
        margin: 0;
        padding: 0;
        white-space: normal;
        word-wrap: break-word;
        word-break: keep-all;
        font-weight: 400;
      }
    }
  }

  /* 두 줄 타이틀 카드 처리 */
  &:nth-child(3), &:nth-child(5), &:nth-child(7) {
    .scope-description {
      top: 142px;
    }
  }

  /* 호버 시 타이틀과 설명 모두 표시 */
  &:hover, &:active, &.active {
    background: #FFFFFF;
    outline: 3px solid #FF4F00;
    outline-offset: -3px;

    @media (max-width: 768px) {
      padding: 20px;
    }

    .scope-card-title {
      color: #FF4F00;
      opacity: 1;
      width: auto;
      max-width: 100%;
      
      &.pc-title {
        @media (min-width: 769px) {
          display: inline-block;
        }
        @media (max-width: 768px) {
          display: none;
        }
      }
      
      &.mobile-title {
        @media (min-width: 769px) {
          display: none;
        }
        @media (max-width: 768px) {
          display: inline-block;
        }
      }
    }

    .scope-image-container {
      opacity: 0;
    }

    .scope-description {
      opacity: 1;
      z-index: 1;
    }
  }

  @media (max-width: 768px) {
    width: 100%;
    height: 168px;
    min-height: 168px;
    padding: 20px;
    border-radius: 20px;
    outline: 1px solid #DCDCDC;
    outline-offset: -1px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    border: none;
    
    &.active {
      height: 168px;
      min-height: 168px;
      outline: 1px solid #FF4F00;
      outline-offset: -1px;
      border: none;
    }
    
    .title-description-gap {
      display: none;
    }
    
    .scope-description {
      position: static;
      margin-top: 16px;
      left: auto;
      right: auto;
      top: auto;
      bottom: auto;
      max-width: 295px;
      width: 295px;
      text-align: left;
      opacity: 0;
      padding: 0;
      font-size: 14px;
      font-weight: 400;
      line-height: 26px;
      transition: opacity 0.3s ease;
      align-self: flex-start;
      
      p {
        margin: 0;
        padding: 0;
        white-space: normal;
        word-wrap: break-word;
        word-break: keep-all;
        font-weight: 400;
      }
    }

    .scope-card-title {
      padding-left: 0;
      transition: color 0.3s ease;
      margin-bottom: 0;
      align-self: flex-start;
      
      @media (max-width: 768px) {
        font-family: Montserrat;
        font-weight: 700;
        font-size: 16px;
        line-height: 28px;
        letter-spacing: -0.01em;
      }
    }
    
    .scope-image-container {
      display: block;
      position: absolute;
      bottom: 20px;
      right: 20px;
      width: 128px;
      height: 128px;
      opacity: 1;
      transition: opacity 0.3s ease;
      z-index: 0;
    }
    
    &:hover, &:active, &.active {
      border: none;
      outline: 1px solid #FF4F00;
      outline-offset: -1px;
      background: #FFFFFF;
      
      .scope-card-title {
        color: #FF4F00;
        opacity: 1;
        width: auto;
        max-width: 100%;
        
        &.pc-title {
          @media (min-width: 769px) {
            display: inline-block;
          }
          @media (max-width: 768px) {
            display: none;
          }
        }
        
        &.mobile-title {
          @media (min-width: 769px) {
            display: none;
          }
          @media (max-width: 768px) {
            display: inline-block;
          }
        }
      }
      
      .scope-image-container {
        opacity: 0;
      }
      
      .scope-description {
        opacity: 1;
        z-index: 1;
      }
    }

    &:not(.active) {
      border: none;
      outline: 1px solid #DCDCDC;
      outline-offset: -1px;
      background: #F8F8F8;
      
      .scope-image-container {
        opacity: 1;
      }
      
      .scope-description {
        opacity: 0;
      }
      
      .scope-card-title {
        color: inherit;
      }
    }

    &.multiline-title.active .scope-card-title br {
      display: none !important;
    }
    
    &.multiline-title.active .scope-card-title {
      white-space: nowrap !important;
    }
  }
`;

export const ScopeCardTitle = styled.span`
  color: #1D1D1D;
  font-size: 24px;
  font-family: Montserrat;
  font-weight: 700;
  line-height: 36px;
  word-wrap: break-word;
  word-break: keep-all;
  position: relative;
  z-index: 1;
  transition: color 0.5s ease;
  display: inline-block;
  class-name: scope-card-title;
  max-width: 100%;
  white-space: normal;

  &.pc-title {
    display: inline-block;
    white-space: normal;
    word-wrap: break-word;
    word-break: keep-all;
    
    @media (max-width: 768px) {
      display: none;
    }
  }
  
  &.mobile-title {
    display: none;
    @media (max-width: 768px) {
      display: block;
      white-space: normal;
      br {
        display: block !important;
      }
    }
  }

  @media (max-width: 768px) {
    padding-left: 0;
    margin-bottom: 0;
    width: auto;
    font-family: Montserrat;
    font-weight: 700;
    font-size: 16px;
    line-height: 28px;
  }
`;

export const ScopeImageContainer = styled.div`
  width: 210px;
  height: 210px;
  position: absolute;
  bottom: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: opacity 0.5s ease;
  class-name: scope-image-container;
  
  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  @media (max-width: 768px) {
    width: 128px;
    height: 128px;
    position: absolute;
    bottom: 20px;
    right: 20px;
    display: flex;
    justify-content: center;
    align-items: center;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      margin: auto;
      display: block;
    }
  }
`;

export const WhatWeDoTab = styled.div`
  width: 100%;
  justify-content: flex-start;
  align-items: center;
  gap: 50px;
  display: inline-flex;
  margin-bottom: 40px;
  padding-top: 160px;

  @media (max-width: 768px) {
    padding-top: 80px;
    margin-bottom: 20px;
    gap: 16px;
  }
`;

export const WhatWeDoTabItem = styled.div<{ active?: boolean }>`
  padding: 7px 0;
  justify-content: center;
  align-items: center;
  gap: 4px;
  display: inline-flex;
  cursor: pointer;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    width: 100%;
    height: 4px;
    background-color: ${props => props.active ? '#FF4F00' : 'transparent'};
    transition: all 0.3s ease;
  }
  
  span {
    color: ${props => props.active ? '#1D1D1D' : '#999999'};
    font-size: 24px;
    font-family: Montserrat;
    font-weight: 900;
    line-height: 36px;
    word-wrap: break-word;
    transition: all 0.3s ease;

    @media (max-width: 768px) {
      font-size: 16px;
      line-height: 24px;
      margin-bottom: 2px;
    }
  }

  &:hover {
    span {
      color: #1D1D1D;
    }
  }

  @media (max-width: 768px) {
    padding: 20px 0 2px 0;
    gap: 0;
  }
`;

export const VoucherWhatWeDoButton = styled.button`
  height: 40px;
  padding: 6px 20px;
  border-radius: 50px;
  border: 1px solid #DCDCDC;
  background: transparent;
  color: #1D1D1D;
  font-size: 16px;
  font-family: Pretendard;
  font-weight: 500;
  line-height: 28px;
  word-wrap: break-word;
  cursor: pointer;
  transition: all 0.3s ease;
  width: fit-content;
  align-self: flex-start;

  &:hover {
    color: #FF4F00;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    align-self: center;
    text-align: center;
    justify-content: center;
    display: flex;
    align-items: center;
  }
`;

export const MediaPartnerSection = styled.div`
  width: 100vw;
  position: relative;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
  display: flex;
  flex-direction: column;
  gap: 40px;
  margin-top: 260px;
  background-color: #F9F9F9;
  ${createFadeInAnimation(0.8)}

  @media (max-width: 768px) {
    padding-bottom: 60px;
    margin-top: 40px;
    gap: 20px;
  }
`;

export const MediaPartnerContent = styled.div`
  width: 100%;
  max-width: 1440px;
  padding: 120px 0 120px 0;
  margin: 0 auto;
  box-sizing: border-box;
  
  @media (max-width: 1920px) {
    padding: 120px 0 120px 0;
  }
  
  @media (max-width: 1700px) {
    padding: 120px 120px 120px 120px;
  }
  
  @media (max-width: 1440px) {
    padding: 120px 80px;
  }

  @media (max-width: 1050) {
    width: 100%;
    padding: 60px 20px 0;
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 60px 20px 0;
  }
`;

export const MediaPartnerTitle = styled.span`
  color: #1D1D1D;
  font-size: 48px;
  font-family: Montserrat;
  font-weight: 700;
  line-height: 70px;
  word-wrap: break-word;

  @media (max-width: 768px) {
    font-size: 32px;
    line-height: 40px;
  }
`;

export const MediaPartnerDescription = styled.span`
  color: #5C5C5C;
  font-size: 24px;
  font-family: Pretendard;
  font-weight: 500;
  line-height: 36px;
  word-wrap: break-word;

  @media (max-width: 768px) {
    font-family: Pretendard;
    font-weight: 400;
    font-size: 16px;
    line-height: 24px;
    letter-spacing: -0.01em;
    margin-top: 0px;
  }
`;

export const TitleContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 10px;
`;

export const MediaPartnerImage1 = styled.img`
  width: 100%;
  object-fit: contain;
  margin-top: 60px;
  padding: 18px 0;
  background-color: #FFFFFF;
  box-sizing: border-box;

  @media (min-width: 769px) {
    width: 100%;
    max-width: 1440px;
    padding: 18px 20px;
  }

  @media (max-width: 768px) {
    width: 100%;
    height: auto;
    margin-top: 60px;
    margin-bottom: 30px;
    padding: 0;
    object-fit: contain;
  }
`;

export const MediaPartnerImage2 = styled.img`
  width: 100%;
  height: auto;
  max-height: 845px;
  object-fit: contain;
  margin-top: 100px;

  @media (max-width: 1050px) and (min-width: 769px) {
    width: 100vw;
    margin-left: calc(-50vw + 50%);
    margin-right: calc(-50vw + 50%);
    object-fit: cover;
  }

  @media (max-width: 768px) {
    width: 100vw;
    height: 221px;
    margin-left: -20px;
    margin-top: 30px;
    object-fit: cover;
  }
`;

export const MobileWhatWeDoContent = styled.div`
  width: 100%;
  margin-top: 40px;
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }
`;

// 모바일 전용 what we do 설명글 스타일드 컴포넌트
export const VoucherWhatWeDoDescription = styled.span`
  color: #1D1D1D;
  font-family: Pretendard;
  font-weight: 400;
  font-size: 16px;
  line-height: 26px;
  letter-spacing: -1%;
  width: 100%;
  white-space: normal;

  @media (max-width: 768px) {
    font-size: 16px;
    font-weight: 300;
    line-height: 28px;
    letter-spacing: -0.04em;

    p {
      margin: 0;
    }
  }
`;